import SingleSelectComponent from "../../components/single-select/single-select.component";
import { transStatus } from "../../config/global-variables";
import IncentiveInputModel from "../../models/incentive-input/incentive-input.model";
import ViewBase from "../view-base.view";

/**
 * Incentive Inputs & Uploads
 *
 * FFIS period inputs: stage a file, inspect what it resolved, validate it to make it live, and
 * record manual adjustments. Menu WM021102, OPERATOR.
 *
 * ============================================================================================
 * UPLOAD THEN VALIDATE - TWO STEPS, AND NOT A UI CHOICE
 * ============================================================================================
 * An upload STAGES its rows with Active = 0 and they enter no calculation. Only validation flips
 * them live, and in the same transaction it marks the previous batch for that scope SUPERSEDED.
 * That gap is where a wrong file gets caught before it changes anybody's pay, so Upload and
 * Validate are separate buttons with the batch's findings readable in between. One button would
 * be fewer clicks and would remove the only chance to notice.
 *
 * ============================================================================================
 * ⚠️ WHY THIS PASTES DATA INSTEAD OF TAKING AN .xlsx FILE
 * ============================================================================================
 * The house pattern for bulk upload posts the raw file as FormData and lets the BACKEND parse it
 * (see gsbn-mapping-management.model.js: body.append('excelTemplate', file)). The FFIS input API
 * does not work that way - every upload route takes a JSON array of typed rows - and this project
 * has no xlsx library, so a real .xlsx cannot be parsed in the browser without adding one.
 *
 * So this page accepts data PASTED from a spreadsheet: select the columns in Excel, copy, paste.
 * The clipboard carries tab-separated text, which is parsed here into the typed rows the API
 * wants. That is a real workflow rather than a stopgap - it is how people normally move a column
 * range out of a sheet - and it needs no new dependency and no change to the frozen route list.
 *
 * It is NOT equivalent to a file upload, and the difference should be settled rather than left:
 *   - it has no filename, so IncentiveUploadBatch records no source document;
 *   - a 6,000-row manpower file is not a realistic paste.
 * Fixing that properly means either a client-side xlsx dependency or new multipart routes, and
 * new routes mean writing to APIMaster again, which is another team's table. Flagged, not chosen.
 *
 * @author FFIS
 */
export default function IncentiveInputView() {
  ViewBase.call(this);

  const PRODUCT_GROUPS = [
    { key: 'MX', value: 'MX' },
    { key: 'AV', value: 'AV' },
  ];

  const PERIOD_TYPES = [
    { key: 'MONTHLY', value: 'MONTHLY' },
    { key: 'WEEKLY', value: 'WEEKLY' },
  ];

  const BATCH_STATUSES = [
    { key: '', value: '(all)' },
    { key: 'STAGED', value: 'STAGED' },
    { key: 'APPLIED', value: 'APPLIED' },
    { key: 'SUPERSEDED', value: 'SUPERSEDED' },
    { key: 'REJECTED', value: 'REJECTED' },
  ];

  const ADJUSTMENT_TYPES = [
    { key: 'CREDIT', value: 'CREDIT' },
    { key: 'DEBIT', value: 'DEBIT' },
    { key: 'OVERRIDE', value: 'OVERRIDE' },
  ];

  /*
   * The four upload types, declared rather than coded four times.
   *
   * `columns` is the paste order and IS the contract with the operator - the header row shown on
   * screen has to match it exactly, because a paste is positional. Required fields are marked so
   * a row missing one is rejected here rather than by the server.
   *
   * `numeric` and `bool` say how to coerce, because a paste is all strings: sending "12" where
   * the DTO wants a decimal works, but sending "" where it wants a nullable decimal does not,
   * and sending "Y" where it wants a bool silently becomes false.
   */
  const UPLOAD_TYPES = {
    MANPOWER: {
      key: 'MANPOWER',
      label: 'Manpower (the population)',
      send: (body) => IncentiveInputModel.uploadManpower(body),
      /* No sourceSystem: IncentiveManpowerUploadRequest does not declare one. Model binding
         would silently ignore it, which is exactly how a field nobody reads survives a rename. */
      sourceSystem: false,
      note: 'THE ONLY WRITER OF THE CALCULATOR POPULATION. A period with no manpower batch '
          + 'calculates nobody and reports success while doing it. positionCode values contain '
          + 'SPACES - "EC REGULAR", "SPV TRD" - and are matched exactly.',
      columns: ['userID', 'siteCode', 'positionCode', 'kamOps', 'gkamStrOps', 'workLevel',
                'assignmentType', 'locationClass', 'storeSizeGroup', 'storePrioTier',
                'activeDays', 'totalPeriodDays', 'performanceGrade', 'resignationNotice',
                'resignationDate', 'noticeSubmitDate', 'complianceKpiPass', 'divisionTransfer'],
      required: ['userID'],
      numeric: ['activeDays', 'totalPeriodDays'],
      bool: ['resignationNotice', 'complianceKpiPass', 'divisionTransfer'],
    },
    PLATFORM: {
      key: 'PLATFORM',
      label: 'Platform actuals (F+ / Trade-In / SC+)',
      send: (body) => IncentiveInputModel.uploadPlatform(body),
      sourceSystem: true,
      note: 'These do NOT come from MCS - the Platform Management team owns the AR% of qty for '
          + 'F+ and Trade-In, so this upload is the only way those figures enter FFIS.',
      columns: ['userID', 'siteCode', 'positionCode', 'financePlusUnits', 'tradeInUnits',
                'scPlusEntryUnits', 'scPlusMmhUnits', 'scPlusPremiumUnits', 'sfPlusUnits',
                'vasPieceCount', 'financePlusArQty', 'tradeInArQty', 'scPlusAttachRate',
                'activeStoreCount', 'activeStore', 'platformPartner'],
      required: ['userID'],
      numeric: ['financePlusUnits', 'tradeInUnits', 'scPlusEntryUnits', 'scPlusMmhUnits',
                'scPlusPremiumUnits', 'sfPlusUnits', 'vasPieceCount', 'financePlusArQty',
                'tradeInArQty', 'scPlusAttachRate', 'activeStoreCount'],
      bool: ['activeStore'],
    },
    KPI: {
      key: 'KPI',
      label: 'KPI measurements (scorecard schemes)',
      send: (body) => IncentiveInputModel.uploadKpi(body),
      sourceSystem: true,
      note: 'An unknown kpiCode is FATAL to the whole file, unlike an unresolvable userID. A KPI '
          + 'with no weight would score zero and quietly lower every total that includes it.',
      columns: ['userID', 'positionCode', 'kpiCode', 'targetValue', 'actualValue',
                'achievementRate', 'scoreValue', 'deductionValue'],
      required: ['userID', 'kpiCode'],
      numeric: ['targetValue', 'actualValue', 'achievementRate', 'scoreValue', 'deductionValue'],
      bool: [],
    },
    ELIGIBLE_MODEL: {
      key: 'ELIGIBLE_MODEL',
      label: 'Model lists (FOCUS / KEY / ELIGIBLE / EXCLUDED / NCME)',
      send: (body) => IncentiveInputModel.uploadEligibleModel(body),
      sourceSystem: false,
      note: 'The procedure REFUSES a file where one model appears on two lists for the same '
          + 'period. EXCLUDED and ELIGIBLE are contradictory instructions about whether a model '
          + 'earns anything, so there is no safe way to pick one.',
      columns: ['listType', 'modelCode', 'modelAlias', 'segment1', 'segment2', 'scPlusBand',
                'channelType', 'amountValue', 'capAmount'],
      required: ['listType', 'modelCode'],
      numeric: ['amountValue', 'capAmount'],
      bool: [],
    },
  };

  const DEFAULT_SCOPE = {
    productGroupCode: 'MX',
    periodType: 'MONTHLY',
    periodCode: '',
  };

  let scope = structuredClone(DEFAULT_SCOPE);
  let batchStatus = '';

  let batches = [];
  let batchDetail = null;
  let selectedBatchId = null;

  let uploadType = 'MANPOWER';
  let pasteText = '';
  let parseErrors = [];
  let parsedRows = [];

  /** Shown, not hidden in a default - see the model's note on why 20 is a placeholder. */
  let rejectThresholdPct = 20;
  let validateComment = '';

  let adjustments = [];
  let adjustmentForm = null;

  const readEnvelope = (result) => (Array.isArray(result) ? result[0] : result?.transResult?.[0]);

  const scopeIsComplete = () => !!(scope.productGroupCode && scope.periodType && scope.periodCode);

  /* ---- the paste parser --------------------------------------------------- */

  /**
   * Parse pasted spreadsheet text into typed rows.
   *
   * Tab first, comma second: a clipboard copy out of Excel is tab-separated, and a comma split
   * would shred any field containing a comma - which store names and reason texts routinely do.
   */
  const parsePaste = () => {
    const spec = UPLOAD_TYPES[uploadType];
    const errors = [];
    const rows = [];

    const lines = (pasteText || '').split(/\r?\n/).filter(l => l.trim().length > 0);

    lines.forEach((line, index) => {
      const sep = line.indexOf('\t') >= 0 ? '\t' : ',';
      const cells = line.split(sep).map(c => c.trim());

      /* A pasted header row is common and harmless - skip it rather than fail on it. */
      if (index === 0 && cells[0]
          && cells[0].toLowerCase() === spec.columns[0].toLowerCase()) {
        return;
      }

      if (cells.length > spec.columns.length) {
        errors.push(`Line ${index + 1}: ${cells.length} columns, expected at most `
          + `${spec.columns.length}. Check the column order above.`);
        return;
      }

      const row = {};
      spec.columns.forEach((col, i) => {
        const raw = cells[i] === undefined ? '' : cells[i];

        if (raw === '') {
          /* Empty stays undefined rather than becoming 0 or false. A blank activeDays means
             "not supplied", and sending 0 would prorate a leader's nominal down to nothing. */
          return;
        }
        if (spec.numeric.indexOf(col) >= 0) {
          const n = Number(raw.replace(/,/g, ''));
          if (Number.isNaN(n)) {
            errors.push(`Line ${index + 1}: ${col} "${raw}" is not a number.`);
            return;
          }
          row[col] = n;
          return;
        }
        if (spec.bool.indexOf(col) >= 0) {
          const t = raw.toLowerCase();
          if (['1', 'y', 'yes', 'true'].indexOf(t) >= 0) { row[col] = true; return; }
          if (['0', 'n', 'no', 'false'].indexOf(t) >= 0) { row[col] = false; return; }
          errors.push(`Line ${index + 1}: ${col} "${raw}" is not a yes/no value.`);
          return;
        }
        row[col] = raw;
      });

      const missing = spec.required.filter(c => row[c] === undefined || row[c] === '');
      if (missing.length) {
        errors.push(`Line ${index + 1}: missing required ${missing.join(', ')}.`);
        return;
      }

      rows.push(row);
    });

    parseErrors = errors;
    parsedRows = rows;
  };

  /* ---- loading ----------------------------------------------------------- */

  const searchBatches = () => {
    this.showLoading();
    IncentiveInputModel.searchBatches({
      productGroupCode: scope.productGroupCode,
      periodType: scope.periodType,
      periodCode: scope.periodCode,
      statusCode: batchStatus || undefined,
    })
      .then((result) => { batches = result || []; })
      .catch((error) => { this.handleError(error); })
      .finally(() => { this.hideLoading(); });
  };

  const loadBatchDetail = (batchId) => {
    this.showLoading();
    IncentiveInputModel.getBatchDetail(batchId)
      .then((result) => {
        batchDetail = {
          header: result?.[0]?.[0] || result?.header?.[0] || null,
          findings: result?.[1] || result?.findings || [],
        };
        selectedBatchId = batchId;
      })
      .catch((error) => { this.handleError(error); })
      .finally(() => { this.hideLoading(); });
  };

  const searchAdjustments = () => {
    this.showLoading();
    IncentiveInputModel.searchAdjustments({
      productGroupCode: scope.productGroupCode,
      periodType: scope.periodType,
      periodCode: scope.periodCode,
    })
      .then((result) => { adjustments = result || []; })
      .catch((error) => { this.handleError(error); })
      .finally(() => { this.hideLoading(); });
  };

  /* ---- upload ------------------------------------------------------------ */

  const onUpload = () => {
    parsePaste();

    if (parseErrors.length) {
      this.showMessage(
        this.getText('EM00000280',
          'The pasted data has ' + parseErrors.length + ' problem(s). Fix them before '
        + 'uploading - nothing was sent.'),
        transStatus.failed);
      return;
    }
    if (!parsedRows.length) {
      this.showMessage(
        this.getText('EM00000281', 'Nothing to upload - paste some rows first.'),
        transStatus.failed);
      return;
    }

    const spec = UPLOAD_TYPES[uploadType];

    this.showConfirm(
      this.getText('EM00000282',
        'Stage ' + parsedRows.length + ' row(s) as a new ' + spec.key + ' batch? They are '
      + 'STAGED and enter no calculation until you validate the batch.'),
      this.getText('LT00000022', 'Save'),
      (e) => {
        if (e !== 'YES') {
          return;
        }
        this.showLoading();
        const body = {
          productGroupCode: scope.productGroupCode,
          periodType: scope.periodType,
          periodCode: scope.periodCode,
          rows: parsedRows,
        };
        /* Only where the request DTO actually declares it - see the per-type flag. */
        if (spec.sourceSystem) {
          body.sourceSystem = 'MANUAL_XLS';
        }

        spec.send(body)
          .then((result) => {
            const trans = readEnvelope(result);
            this.showMessage(
              this.getText(trans?.code, trans?.message),
              trans?.transStatus,
              () => {
                if (trans?.transStatus === transStatus.success) {
                  pasteText = '';
                  parsedRows = [];
                  searchBatches();
                  const newId = Number(trans?.id);
                  if (newId) {
                    loadBatchDetail(newId);
                  }
                }
              });
          })
          .catch((error) => { this.handleError(error); })
          .finally(() => { this.hideLoading(); });
      });
  };

  /* ---- validate ---------------------------------------------------------- */

  const onValidate = (batchId) => {
    this.showConfirm(
      this.getText('EM00000283',
        'Validating makes batch ' + batchId + ' LIVE and marks the previous batch for this '
      + 'scope SUPERSEDED. Rows will enter the next calculation. Continue?'),
      this.getText('LT00000022', 'Save'),
      (e) => {
        if (e !== 'YES') {
          return;
        }
        this.showLoading();
        IncentiveInputModel.validateBatch({
          uploadBatchId: batchId,
          /* Sent explicitly. An omitted value takes the procedure's default of 20; an explicit
             null used to make the guard comparison UNKNOWN so it silently did not fire. */
          rejectThresholdPct: Number(rejectThresholdPct),
          comment: validateComment,
        })
          .then((result) => {
            const trans = readEnvelope(result);
            this.showMessage(
              this.getText(trans?.code, trans?.message),
              trans?.transStatus,
              () => { searchBatches(); loadBatchDetail(batchId); });
          })
          .catch((error) => { this.handleError(error); })
          .finally(() => { this.hideLoading(); });
      });
  };

  /* ---- adjustments ------------------------------------------------------- */

  const blankAdjustment = () => ({
    adjustmentId: 0,
    productGroupCode: scope.productGroupCode,
    periodType: scope.periodType,
    periodCode: scope.periodCode,
    userID: '',
    siteCode: '',
    componentCode: '',
    adjustmentType: 'CREDIT',
    adjustmentAmount: '',
    currency: '',
    reasonCode: '',
    reasonText: '',
  });

  const onSaveAdjustment = () => {
    const f = adjustmentForm;
    const missing = ['userID', 'adjustmentType', 'adjustmentAmount', 'currency',
                     'reasonCode', 'reasonText'].filter(k => !f[k] && f[k] !== 0);

    if (missing.length) {
      this.showMessage(
        this.getText('EM00000284',
          'These are required: ' + missing.join(', ') + '. An adjustment without a reason is a '
        + 'payment nobody can account for later.'),
        transStatus.failed);
      return;
    }

    this.showLoading();
    IncentiveInputModel.saveAdjustment({ ...f, adjustmentAmount: Number(f.adjustmentAmount) })
      .then((result) => {
        const trans = readEnvelope(result);
        this.showMessage(
          this.getText(trans?.code, trans?.message),
          trans?.transStatus,
          () => {
            if (trans?.transStatus === transStatus.success) {
              adjustmentForm = null;
              searchAdjustments();
            }
          });
      })
      .catch((error) => { this.handleError(error); })
      .finally(() => { this.hideLoading(); });
  };

  /* ---- render helpers ---------------------------------------------------- */

  const num = (v, dp) => (v === null || v === undefined
    ? '-'
    : Number(v).toLocaleString(undefined, {
        minimumFractionDigits: dp || 0, maximumFractionDigits: dp || 0 }));

  const statusBadge = (status) => {
    const cls = status === 'APPLIED' ? 'bg-success'
      : status === 'STAGED' ? 'bg-warning text-dark'
      : status === 'REJECTED' ? 'bg-danger'
      : 'bg-secondary';
    return m('span.badge', { class: cls }, status || '-');
  };

  /* ---- sections ---------------------------------------------------------- */

  const scopeSection = () => m('.card.mb-3', [
    m('.card-header.fw-semibold', this.getText('LT00000263', '1. Scope')),
    m('.card-body',
      m('.row.g-4', [
        m('.col-md-3.position-relative', [
          m('label.form-label[for=inProductGroup]',
            this.getText('LT00000008', 'Product Group')),
          m(SingleSelectComponent, {
            id: 'inProductGroup',
            items: PRODUCT_GROUPS,
            selectedItem: scope.productGroupCode,
            checkmark: true,
            keyOnly: true,
            onSelect: (item) => { scope.productGroupCode = item; },
          }),
        ]),
        m('.col-md-3.position-relative', [
          m('label.form-label[for=inPeriodType]', this.getText('LT00000264', 'Period Type')),
          m(SingleSelectComponent, {
            id: 'inPeriodType',
            items: PERIOD_TYPES,
            selectedItem: scope.periodType,
            checkmark: true,
            keyOnly: true,
            onSelect: (item) => { scope.periodType = item; },
          }),
        ]),
        m('.col-md-2', [
          m('label.form-label', this.getText('LT00000265', 'Period Code')),
          m('input.form-control.form-control-bg', {
            value: scope.periodCode,
            placeholder: '202603 / W10',
            oninput: (e) => { scope.periodCode = e.target.value; },
          }),
        ]),
        m('.col-md-2.position-relative', [
          m('label.form-label[for=inBatchStatus]', this.getText('LT00000293', 'Status')),
          m(SingleSelectComponent, {
            id: 'inBatchStatus',
            items: BATCH_STATUSES,
            selectedItem: batchStatus,
            checkmark: true,
            keyOnly: true,
            onSelect: (item) => { batchStatus = item; },
          }),
        ]),
        m('.col-md-2.d-flex.align-items-end',
          m('button.btn.btn-outline-primary.w-100', {
            disabled: !scopeIsComplete(),
            onclick: () => { searchBatches(); searchAdjustments(); },
          }, this.getText('LT00000021', 'Search'))),
      ])),
  ]);

  const uploadSection = () => {
    const spec = UPLOAD_TYPES[uploadType];

    return m('.card.mb-3', [
      m('.card-header.fw-semibold', this.getText('LT00000310', '2. Stage an upload')),
      m('.card-body', [
        m('.row.g-3.mb-3', [
          m('.col-md-5.position-relative', [
            m('label.form-label[for=inUploadType]',
              this.getText('LT00000311', 'Upload type')),
            m(SingleSelectComponent, {
              id: 'inUploadType',
              items: Object.keys(UPLOAD_TYPES).map(k => ({
                key: k, value: UPLOAD_TYPES[k].label })),
              selectedItem: uploadType,
              checkmark: true,
              keyOnly: true,
              onSelect: (item) => {
                uploadType = item;
                pasteText = '';
                parsedRows = [];
                parseErrors = [];
              },
            }),
          ]),
        ]),

        /* Every type carries a warning that is specific to it. A generic "check your file"
           teaches nobody which mistake this particular upload punishes. */
        m('.alert.alert-warning.small', spec.note),

        m('label.form-label', this.getText('LT00000312',
          'Paste from your spreadsheet - select the columns below, copy, paste here')),
        m('.small.text-muted.mb-2',
          m('code', spec.columns.join('  |  '))),
        m('.small.text-muted.mb-2', this.getText('EM00000285',
          'Order matters - a paste is positional. A pasted header row is skipped. Blank cells '
        + 'stay empty rather than becoming 0 or No.')),

        m('textarea.form-control.form-control-bg.mb-2', {
          rows: 8,
          value: pasteText,
          placeholder: spec.columns.slice(0, 4).join('\t') + '\t...',
          oninput: (e) => { pasteText = e.target.value; },
          onblur: () => { parsePaste(); },
        }),

        m('.d-flex.gap-2.align-items-center', [
          m('button.btn.btn-outline-secondary', {
            onclick: () => { parsePaste(); },
          }, this.getText('LT00000313', 'Check')),
          m('button.btn.btn-primary', {
            disabled: !scopeIsComplete(),
            onclick: onUpload,
          }, this.getText('LT00000314', 'Stage upload')),
          parsedRows.length > 0 && m('span.text-success.small',
            num(parsedRows.length) + this.getText('LT00000315', ' row(s) ready')),
        ]),

        parseErrors.length > 0 && m('.alert.alert-danger.mt-3.mb-0', [
          m('div.fw-semibold', this.getText('LT00000316', 'Nothing was sent')),
          m('ul.mb-0.small', parseErrors.slice(0, 25).map(e => m('li', e))),
          parseErrors.length > 25 && m('.small.mt-1',
            '... and ' + (parseErrors.length - 25) + ' more'),
        ]),
      ]),
    ]);
  };

  const batchesSection = () => m('.card.mb-3', [
    m('.card-header.fw-semibold.d-flex.justify-content-between.align-items-center', [
      m('span', this.getText('LT00000317', '3. Batches - validate to make one live')),
      m('.d-flex.align-items-center.gap-2', [
        m('label.form-label.mb-0.small', this.getText('LT00000318', 'Reject threshold %')),
        m('input.form-control.form-control-sm', {
          type: 'number', min: 0, max: 100, style: 'width: 5rem',
          value: rejectThresholdPct,
          oninput: (e) => { rejectThresholdPct = e.target.value; },
        }),
      ]),
    ]),
    m('.card-body.p-0', [
      m('.px-3.pt-3.small.text-muted', this.getText('EM00000286',
        'The threshold is the share of rows whose userID may fail to resolve before the batch '
      + 'is refused. It is the guard against applying a file for the wrong period or subsidiary. '
      + '20 is a PLACEHOLDER - no business tolerance has been agreed.')),

      batches.length === 0
        ? m('.p-3.text-muted', this.getText('LT00000319', 'No batches for this scope.'))
        : m('.table-responsive', m('table.table.table-sm.table-hover.mb-0', [
            m('thead', m('tr', [
              m('th', this.getText('LT00000320', 'Batch')),
              m('th', this.getText('LT00000311', 'Type')),
              m('th', this.getText('LT00000293', 'Status')),
              m('th.text-end', this.getText('LT00000321', 'Rows')),
              m('th.text-end', this.getText('LT00000322', 'Unresolved')),
              m('th', this.getText('LT00000323', 'Uploaded by')),
              m('th', ''),
            ])),
            m('tbody', batches.map(row => m('tr.cursor-pointer', {
              class: selectedBatchId === row.uploadBatchId ? 'table-active' : '',
              onclick: () => { loadBatchDetail(row.uploadBatchId); },
            }, [
              m('td', row.uploadBatchId),
              m('td.small', row.uploadType),
              m('td', statusBadge(row.statusCode)),
              m('td.text-end', num(row.rowCount)),
              m('td.text-end',
                row.unresolvedCount > 0
                  ? m('span.text-danger.fw-semibold', num(row.unresolvedCount))
                  : num(row.unresolvedCount)),
              m('td.small.text-muted', row.createUserId),
              m('td.text-end',
                row.statusCode === 'STAGED' && m('button.btn.btn-sm.btn-primary', {
                  onclick: (e) => { e.stopPropagation(); onValidate(row.uploadBatchId); },
                }, this.getText('LT00000324', 'Validate'))),
            ]))),
          ])),
    ]),
  ]);

  const batchDetailSection = () => {
    if (!batchDetail) {
      return null;
    }
    return m('.card.mb-3', [
      m('.card-header.fw-semibold',
        this.getText('LT00000325', 'Batch detail') + ' - ' + (selectedBatchId || '')),
      m('.card-body', [
        m('.mb-3',
          m('label.form-label', this.getText('LT00000326', 'Validation comment')),
          m('input.form-control.form-control-bg', {
            value: validateComment,
            placeholder: this.getText('LT00000327',
              'What you checked before making this live'),
            oninput: (e) => { validateComment = e.target.value; },
          })),

        m('div.fw-semibold.mb-2', this.getText('LT00000328', 'Findings')),
        batchDetail.findings.length === 0
          ? m('.text-muted.small', this.getText('LT00000329',
              'No findings - every row resolved.'))
          : m('.table-responsive', m('table.table.table-sm', [
              m('thead', m('tr', [
                m('th', this.getText('LT00000330', 'Row')),
                m('th', this.getText('LT00000305', 'Employee')),
                m('th', this.getText('LT00000308', 'Reason')),
              ])),
              m('tbody', batchDetail.findings.map(row => m('tr', [
                m('td', row.rowNumber),
                m('td.small', row.userID || row.userId),
                m('td.small', row.findingCode || row.message),
              ]))),
            ])),
      ]),
    ]);
  };

  const adjustmentsSection = () => m('.card.mb-3', [
    m('.card-header.fw-semibold.d-flex.justify-content-between.align-items-center', [
      m('span', this.getText('LT00000331', '4. Manual adjustments')),
      m('button.btn.btn-sm.btn-outline-primary', {
        disabled: !scopeIsComplete(),
        onclick: () => { adjustmentForm = blankAdjustment(); },
      }, this.getText('LT00000058', 'Create')),
    ]),
    m('.card-body', [
      m('.alert.alert-info.small', this.getText('EM00000287',
        'An adjustment is NOT visible on the promoter progress screen - the daily snapshot has '
      + 'no column for it. It appears in the final payout and the breakdown report. An adjusted '
      + 'promoter comparing the app against their payslip finds a difference the app cannot '
      + 'explain.')),

      adjustments.length === 0
        ? m('.text-muted.small', this.getText('LT00000332', 'No adjustments for this scope.'))
        : m('.table-responsive', m('table.table.table-sm', [
            m('thead', m('tr', [
              m('th', this.getText('LT00000305', 'Employee')),
              m('th', this.getText('LT00000333', 'Type')),
              m('th.text-end', this.getText('LT00000307', 'Amount')),
              m('th', this.getText('LT00000308', 'Reason')),
              m('th', this.getText('LT00000293', 'Status')),
            ])),
            m('tbody', adjustments.map(row => m('tr', [
              m('td', row.userID || row.userId),
              m('td.small', row.adjustmentType),
              m('td.text-end', num(row.adjustmentAmount, 2)),
              m('td.small', row.reasonCode + (row.reasonText ? ' - ' + row.reasonText : '')),
              m('td', statusBadge(row.statusCode)),
            ]))),
          ])),

      adjustmentForm && m('.border.rounded.p-3.mt-3', [
        m('.row.g-3', [
          m('.col-md-3', [
            m('label.form-label', this.getText('LT00000305', 'Employee')),
            m('input.form-control.form-control-bg', {
              value: adjustmentForm.userID,
              oninput: (e) => { adjustmentForm.userID = e.target.value; },
            }),
          ]),
          m('.col-md-3.position-relative', [
            m('label.form-label[for=adjType]', this.getText('LT00000333', 'Type')),
            m(SingleSelectComponent, {
              id: 'adjType',
              items: ADJUSTMENT_TYPES,
              selectedItem: adjustmentForm.adjustmentType,
              checkmark: true,
              keyOnly: true,
              onSelect: (item) => { adjustmentForm.adjustmentType = item; },
            }),
          ]),
          m('.col-md-3', [
            m('label.form-label', this.getText('LT00000307', 'Amount')),
            m('input.form-control.form-control-bg', {
              type: 'number',
              value: adjustmentForm.adjustmentAmount,
              oninput: (e) => { adjustmentForm.adjustmentAmount = e.target.value; },
            }),
          ]),
          m('.col-md-3', [
            m('label.form-label', this.getText('LT00000334', 'Currency')),
            m('input.form-control.form-control-bg', {
              value: adjustmentForm.currency,
              placeholder: 'IDR / THB / VND',
              oninput: (e) => { adjustmentForm.currency = e.target.value; },
            }),
          ]),
          m('.col-md-3', [
            m('label.form-label', this.getText('LT00000335', 'Reason code')),
            m('input.form-control.form-control-bg', {
              value: adjustmentForm.reasonCode,
              oninput: (e) => { adjustmentForm.reasonCode = e.target.value; },
            }),
          ]),
          m('.col-md-9', [
            m('label.form-label', this.getText('LT00000336', 'Reason')),
            m('input.form-control.form-control-bg', {
              value: adjustmentForm.reasonText,
              placeholder: this.getText('LT00000337',
                'Why this adjustment exists - it is the only account of it later'),
              oninput: (e) => { adjustmentForm.reasonText = e.target.value; },
            }),
          ]),
        ]),
        m('.d-flex.gap-2.mt-3', [
          m('button.btn.btn-primary', { onclick: onSaveAdjustment },
            this.getText('LT00000022', 'Save')),
          m('button.btn.btn-outline-secondary', {
            onclick: () => { adjustmentForm = null; },
          }, this.getText('LT00000023', 'Cancel')),
        ]),
      ]),
    ]),
  ]);

  /* ---- view -------------------------------------------------------------- */

  return {
    oninit: () => {
      /* Nothing loads until a scope is chosen - a blank period code would list every batch. */
    },

    view: () => {
      return [
        scopeSection(),
        uploadSection(),
        batchesSection(),
        batchDetailSection(),
        adjustmentsSection(),
        this.getBaseViews(),
      ];
    },
  };
}
